import { useEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Maximize2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Full-screen product image lightbox with:
 * - Click / tap to open (via `open` prop from parent)
 * - Prev / next controls + arrow-key navigation
 * - Zoom in / out buttons (desktop) + double-tap zoom (touch)
 * - Native pinch-to-zoom on touch (via `touch-action: pinch-zoom` on wrapper)
 * - Drag-to-pan when zoomed
 * - Close on ESC / X / backdrop
 *
 * Preserves the existing gallery thumbnails — this only opens on demand.
 */
export const ProductImageZoom = ({ open, images = [], startIndex = 0, onClose, alt = "" }) => {
  const [idx, setIdx] = useState(startIndex);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragging = useRef(null);

  useEffect(() => { if (open) { setIdx(startIndex); resetView(); } }, [open, startIndex]);

  const resetView = () => { setScale(1); setOffset({ x: 0, y: 0 }); };

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "+" || e.key === "=") zoomIn();
      if (e.key === "-") zoomOut();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, images.length]);

  const next = () => { resetView(); setIdx((i) => (i + 1) % (images.length || 1)); };
  const prev = () => { resetView(); setIdx((i) => (i - 1 + (images.length || 1)) % (images.length || 1)); };
  const zoomIn = () => setScale((s) => Math.min(4, +(s + 0.5).toFixed(2)));
  const zoomOut = () => setScale((s) => { const n = Math.max(1, +(s - 0.5).toFixed(2)); if (n === 1) setOffset({ x: 0, y: 0 }); return n; });

  const onMouseDown = (e) => {
    if (scale <= 1) return;
    dragging.current = { x: e.clientX - offset.x, y: e.clientY - offset.y };
  };
  const onMouseMove = (e) => {
    if (!dragging.current) return;
    setOffset({ x: e.clientX - dragging.current.x, y: e.clientY - dragging.current.y });
  };
  const onMouseUp = () => { dragging.current = null; };

  // Double-tap to zoom on touch
  const lastTap = useRef(0);
  const onImgClick = (e) => {
    const now = Date.now();
    if (now - lastTap.current < 300) {
      // Double tap
      if (scale > 1) resetView(); else setScale(2);
    }
    lastTap.current = now;
  };

  const img = images[idx] || "";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          role="dialog"
          aria-modal="true"
          data-testid="pdp-zoom"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[90] bg-black/95 flex items-center justify-center"
          onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
        >
          {/* Top bar */}
          <div className="absolute top-0 inset-x-0 z-10 flex items-center justify-between px-4 py-3">
            <div className="text-white/70 text-xs tabnum tracking-widest overline">
              {images.length > 1 ? `${idx + 1} / ${images.length}` : ""}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={zoomOut}
                disabled={scale <= 1}
                aria-label="Zoom out"
                data-testid="pdp-zoom-out"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center disabled:opacity-30"
              >
                <ZoomOut size={16} />
              </button>
              <button
                type="button"
                onClick={zoomIn}
                disabled={scale >= 4}
                aria-label="Zoom in"
                data-testid="pdp-zoom-in"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center disabled:opacity-30"
              >
                <ZoomIn size={16} />
              </button>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                data-testid="pdp-zoom-close"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Prev / next */}
          {images.length > 1 && (
            <>
              <button
                type="button"
                onClick={prev}
                aria-label="Previous image"
                data-testid="pdp-zoom-prev"
                className="absolute left-2 md:left-6 z-10 w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                type="button"
                onClick={next}
                aria-label="Next image"
                data-testid="pdp-zoom-next"
                className="absolute right-2 md:right-6 z-10 w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
              >
                <ChevronRight size={18} />
              </button>
            </>
          )}

          {/* Image */}
          <div
            className="relative w-full h-full flex items-center justify-center px-4 md:px-16 py-14 select-none"
            style={{ touchAction: "pinch-zoom" }}
            onMouseDown={onMouseDown}
          >
            <img
              src={img}
              alt={alt}
              onClick={onImgClick}
              data-testid="pdp-zoom-img"
              draggable={false}
              className="max-w-full max-h-full object-contain will-change-transform"
              style={{
                transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
                transformOrigin: "center",
                transition: dragging.current ? "none" : "transform 220ms cubic-bezier(0.7, 0, 0.2, 1)",
                cursor: scale > 1 ? (dragging.current ? "grabbing" : "grab") : "zoom-in",
              }}
            />
          </div>

          {/* Bottom hint */}
          <div className="absolute bottom-3 inset-x-0 text-center text-white/40 text-[10px] tracking-widest uppercase pointer-events-none">
            <span className="hidden md:inline">Double-click to zoom · Drag to pan · Esc to close</span>
            <span className="md:hidden">Pinch to zoom · Swipe controls · Tap × to close</span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProductImageZoom;
